import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { Permission } from '@/modules/auth/permissions/permissions'
import { ProtectedRoute } from '@/router/ProtectedRoute'
import { PermissionRoute } from '@/router/PermissionRoute'
import { PublicRoute } from '@/router/PublicRoute'
import { DashboardLayout } from '@/shared/components/layouts/DashboardLayout'
import { LoginPage } from '@/pages/LoginPage'
import { ClientsListPage } from '@/pages/ClientsListPage'
import { ClientDetailPage } from '@/pages/ClientDetailPage'
import { ClientCreatePage } from '@/pages/ClientCreatePage'
import { ClientEditPage } from '@/pages/ClientEditPage'
import { AddPointPage } from '@/pages/AddPointPage'
import { RedeemPointPage } from '@/pages/RedeemPointPage'
import { UsersListPage } from '@/pages/UsersListPage'
import { UserCreatePage } from '@/pages/UserCreatePage'
import { UserEditPage } from '@/pages/UserEditPage'
import { PointsListPage } from '@/pages/PointsListPage'
import { BranchesListPage } from '@/pages/BranchesListPage'
import { BranchCreatePage } from '@/pages/BranchCreatePage'
import { BranchEditPage } from '@/pages/BranchEditPage'
import { ProductsPage } from '@/pages/ProductsPage'
import { ConfigPage } from '@/pages/ConfigPage'
import { HelpPage } from '@/pages/HelpPage'
import { EmailsPage } from '@/pages/EmailsPage'
import { BenefitRulesListPage } from '@/pages/BenefitRulesListPage'
import { BenefitRuleCreatePage } from '@/pages/BenefitRuleCreatePage'
import { BenefitRuleDetailPage } from '@/pages/BenefitRuleDetailPage'
import { BenefitRuleEditPage } from '@/pages/BenefitRuleEditPage'
import { PointDetailPage } from '@/pages/PointDetailPage'
import { VoucherRedeemPage } from '@/pages/VoucherRedeemPage'
import { BenefitsBackfillPage } from '@/pages/BenefitsBackfillPage'
import { CronLogsListPage } from '@/pages/CronLogsListPage'
import { CronLogDetailPage } from '@/pages/CronLogDetailPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { ForbiddenPage } from '@/pages/ForbiddenPage'

export function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth)

  // Verify stored token on app startup (mirrors Angular constructor behavior)
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/auth/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes — wrapped in DashboardLayout */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/clients" replace />} />

        {/* Clients CRUD — User + Admin */}
        <Route path="/clients" element={<ClientsListPage />} />
        <Route path="/clients/new" element={<ClientCreatePage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/clients/:id/edit" element={<ClientEditPage />} />

        {/* Points — User + Admin */}
        <Route path="/points/add" element={<AddPointPage />} />
        <Route path="/points/redeem" element={<RedeemPointPage />} />
        <Route path="/points/:id" element={<PointDetailPage />} />
        <Route path="/points" element={<PointsListPage />} />

        {/* Users CRUD — Admin only */}
        <Route path="/users" element={<PermissionRoute permission={Permission.USERS_VIEW}><UsersListPage /></PermissionRoute>} />
        <Route path="/users/new" element={<PermissionRoute permission={Permission.USERS_CREATE}><UserCreatePage /></PermissionRoute>} />
        <Route path="/users/:id/edit" element={<PermissionRoute permission={Permission.USERS_EDIT}><UserEditPage /></PermissionRoute>} />

        {/* Branch Offices CRUD — Admin only */}
        <Route path="/branches" element={<PermissionRoute permission={Permission.BRANCHES_VIEW}><BranchesListPage /></PermissionRoute>} />
        <Route path="/branches/new" element={<PermissionRoute permission={Permission.BRANCHES_CREATE}><BranchCreatePage /></PermissionRoute>} />
        <Route path="/branches/:id/edit" element={<PermissionRoute permission={Permission.BRANCHES_EDIT}><BranchEditPage /></PermissionRoute>} />

        {/* Benefits CRUD — Admin only */}
        <Route path="/benefits" element={<PermissionRoute permission={Permission.BENEFITS_VIEW}><BenefitRulesListPage /></PermissionRoute>} />
        <Route path="/benefits/new" element={<PermissionRoute permission={Permission.BENEFITS_CREATE}><BenefitRuleCreatePage /></PermissionRoute>} />
        <Route path="/benefits/:id" element={<PermissionRoute permission={Permission.BENEFITS_VIEW}><BenefitRuleDetailPage /></PermissionRoute>} />
        <Route path="/benefits/:id/edit" element={<PermissionRoute permission={Permission.BENEFITS_EDIT}><BenefitRuleEditPage /></PermissionRoute>} />
        <Route path="/benefits/backfill" element={<PermissionRoute permission={Permission.BENEFITS_BACKFILL}><BenefitsBackfillPage /></PermissionRoute>} />

        {/* Vouchers — User + Admin */}
        <Route path="/vouchers/redeem" element={<VoucherRedeemPage />} />

        {/* Products — User + Admin */}
        <Route path="/products" element={<ProductsPage />} />

        {/* Config — Admin only */}
        <Route path="/config" element={<PermissionRoute permission={Permission.CONFIG_VIEW}><ConfigPage /></PermissionRoute>} />

        {/* Emails — Admin only */}
        <Route path="/emails" element={<PermissionRoute permission={Permission.EMAILS_VIEW}><EmailsPage /></PermissionRoute>} />

        {/* Cron Logs — Admin only */}
        <Route path="/cron-logs" element={<PermissionRoute permission={Permission.CRON_LOGS_VIEW}><CronLogsListPage /></PermissionRoute>} />
        <Route path="/cron-logs/:id" element={<PermissionRoute permission={Permission.CRON_LOGS_VIEW}><CronLogDetailPage /></PermissionRoute>} />

        {/* Help — everyone */}
        <Route path="/help" element={<HelpPage />} />

        {/* 403 Forbidden */}
        <Route path="/forbidden" element={<ForbiddenPage />} />
      </Route>

      {/* 404 Page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
